import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Save, ArrowLeft, Bug, Lightbulb, Wrench, Search, Sparkles, CircleDot, Calendar, User, Paperclip, X } from 'lucide-react'
import { supportTaskApi } from '../services/api'
import { showError } from '../utils/showError'
import RichTextEditor from '../components/RichTextEditor'
import {
  PageHeader,
  LoadingSpinner,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormField,
} from '../components/common'

const typeOptions = [
  { value: 1, label: 'Bug', icon: Bug, color: 'text-red-500 bg-red-50 border-red-200' },
  { value: 2, label: 'Feature', icon: Sparkles, color: 'text-purple-500 bg-purple-50 border-purple-200' },
  { value: 3, label: 'Improvement', icon: Lightbulb, color: 'text-amber-500 bg-amber-50 border-amber-200' },
  { value: 4, label: 'Maintenance', icon: Wrench, color: 'text-gray-500 bg-gray-50 border-gray-200' },
  { value: 5, label: 'Investigation', icon: Search, color: 'text-blue-500 bg-blue-50 border-blue-200' },
  { value: 6, label: 'Other', icon: CircleDot, color: 'text-gray-400 bg-gray-50 border-gray-200' },
]

const priorityOptions = [
  { value: 1, label: 'Low', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  { value: 2, label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 3, label: 'High', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 4, label: 'Critical', color: 'bg-red-50 text-red-700 border-red-200' },
]

const scopeOptions = [
  { key: 'projectScopeApi', label: 'API' },
  { key: 'projectScopeMobile', label: 'Mobile' },
  { key: 'projectScopePortal', label: 'Portal' },
]

const QUICK_ASSIGNEES = ['saman', 'mehran']

const getDateString = (date) => date.toISOString().split('T')[0]

const dueDatePresets = [
  { label: 'Today', fn: () => getDateString(new Date()) },
  { label: 'Tomorrow', fn: () => { const d = new Date(); d.setDate(d.getDate() + 1); return getDateString(d) } },
  { label: 'Next Week', fn: () => { const d = new Date(); d.setDate(d.getDate() + 7); return getDateString(d) } },
  { label: '2 Weeks', fn: () => { const d = new Date(); d.setDate(d.getDate() + 14); return getDateString(d) } },
  { label: 'Next Month', fn: () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return getDateString(d) } },
]

const CreateTask = () => {
  const navigate = useNavigate()
  const { supportTaskId } = useParams()
  const [searchParams] = useSearchParams()
  const isEdit = !!supportTaskId
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEdit)
  const [errors, setErrors] = useState({})
  const [members, setMembers] = useState([])
  const [pendingAttachments, setPendingAttachments] = useState([])
  const attachmentInputRef = useRef(null)

  const [formData, setFormData] = useState({
    title: searchParams.get('title') || '',
    description: '',
    taskType: 1,
    priority: 2,
    projectScopeApi: false,
    projectScopeMobile: false,
    projectScopePortal: false,
    assignedToTaskMemberId: '',
    sentryIssueId: searchParams.get('sentryIssueId') || '',
    sentryIssueUrl: searchParams.get('sentryIssueUrl') || '',
    dueDate: '',
  })

  useEffect(() => {
    loadMembers()
    if (isEdit) loadTask()
  }, [supportTaskId])

  const loadMembers = async () => {
    try {
      const response = await supportTaskApi.getMembers()
      setMembers(response.data || [])
    } catch (error) {
      console.error('Error loading task members:', error)
    }
  }

  const loadTask = async () => {
    try {
      setInitialLoading(true)
      const response = await supportTaskApi.get(supportTaskId)
      const task = response.data
      setFormData({
        title: task.title || '',
        description: task.description || '',
        taskType: task.taskType,
        priority: task.priority,
        projectScopeApi: !!(task.projectScope & 1),
        projectScopeMobile: !!(task.projectScope & 2),
        projectScopePortal: !!(task.projectScope & 4),
        assignedToTaskMemberId: task.assignedToTaskMemberId ? String(task.assignedToTaskMemberId) : '',
        sentryIssueId: task.sentryIssueId || '',
        sentryIssueUrl: task.sentryIssueUrl || '',
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
      })
    } catch (error) {
      console.error('Error loading task:', error)
    } finally {
      setInitialLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleImageUpload = async (file) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await supportTaskApi.uploadTempImage(fd)
    return res.data.url
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.title.trim()) newErrors.title = 'Title is required'
    if (formData.title.length > 500) newErrors.title = 'Title must be 500 characters or less'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      setLoading(true)
      let projectScope = 0
      if (formData.projectScopeApi) projectScope |= 1
      if (formData.projectScopeMobile) projectScope |= 2
      if (formData.projectScopePortal) projectScope |= 4

      const payload = {
        title: formData.title,
        description: formData.description || null,
        taskType: parseInt(formData.taskType),
        priority: parseInt(formData.priority),
        projectScope,
        assignedToTaskMemberId: formData.assignedToTaskMemberId ? parseInt(formData.assignedToTaskMemberId) : null,
        sentryIssueId: formData.sentryIssueId || null,
        sentryIssueUrl: formData.sentryIssueUrl || null,
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
      }

      let response
      if (isEdit) {
        response = await supportTaskApi.update(supportTaskId, payload)
      } else {
        response = await supportTaskApi.create(payload)
      }

      // Upload pending attachments after creation
      const newTaskId = response.data.supportTaskId
      if (!isEdit && pendingAttachments.length > 0) {
        for (const file of pendingAttachments) {
          try {
            const formData = new FormData()
            formData.append('file', file)
            await supportTaskApi.uploadAttachment(newTaskId, formData)
          } catch (uploadErr) {
            console.error('Error uploading attachment:', uploadErr)
          }
        }
      }

      navigate(`/tasks/${newTaskId}`)
    } catch (error) {
      console.error('Error saving task:', error)
      showError(error)
    } finally {
      setLoading(false)
    }
  }

  // Find quick-assignee members by name match
  const quickAssignees = members.filter(m =>
    QUICK_ASSIGNEES.some(name => (m.name || '').toLowerCase().startsWith(name))
  )

  if (initialLoading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEdit ? 'Edit Task' : 'Create Task'}
        action={
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back
          </Button>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content — left 2/3 */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <Card>
              <CardBody>
                <FormField
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  error={errors.title}
                  required
                  placeholder="Brief description of the task"
                />
              </CardBody>
            </Card>

            {/* Description */}
            <Card>
              <CardHeader title="Description" />
              <CardBody>
                <RichTextEditor
                  content={formData.description}
                  onChange={(html) => handleInputChange('description', html)}
                  onMediaUpload={handleImageUpload}
                  placeholder="Detailed description... (paste images directly)"
                />
              </CardBody>
            </Card>

            {/* Attachments (create mode only) */}
            {!isEdit && (
              <Card>
                <CardHeader title="Attachments" />
                <CardBody>
                  <div className="space-y-3">
                    <div>
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt"
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || [])
                          if (files.length) setPendingAttachments(prev => [...prev, ...files])
                          e.target.value = ''
                        }}
                      />
                      <Button variant="secondary" size="small" type="button" onClick={() => attachmentInputRef.current?.click()}>
                        <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                        Add Files
                      </Button>
                    </div>
                    {pendingAttachments.length > 0 && (
                      <div className="space-y-1.5">
                        {pendingAttachments.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2 min-w-0">
                              <Paperclip size={14} className="text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700 truncate">{file.name}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                {(file.size / 1024).toFixed(0)} KB
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                              className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Sentry Info */}
            {(formData.sentryIssueId || formData.sentryIssueUrl) && (
              <Card>
                <CardHeader title="Linked Sentry Issue" />
                <CardBody>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <FormField
                      label="Sentry Issue ID"
                      name="sentryIssueId"
                      value={formData.sentryIssueId}
                      onChange={(e) => handleInputChange('sentryIssueId', e.target.value)}
                      disabled={!!searchParams.get('sentryIssueId')}
                    />
                    <FormField
                      label="Sentry Issue URL"
                      name="sentryIssueUrl"
                      value={formData.sentryIssueUrl}
                      onChange={(e) => handleInputChange('sentryIssueUrl', e.target.value)}
                      disabled={!!searchParams.get('sentryIssueUrl')}
                    />
                  </div>
                </CardBody>
              </Card>
            )}
          </div>

          {/* Sidebar — right 1/3 */}
          <div className="space-y-6">
            {/* Type picker */}
            <Card>
              <CardHeader title="Type" />
              <CardBody>
                <div className="grid grid-cols-2 gap-2">
                  {typeOptions.map(opt => {
                    const Icon = opt.icon
                    const selected = parseInt(formData.taskType) === opt.value
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleInputChange('taskType', opt.value)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                          selected
                            ? `${opt.color} ring-2 ring-offset-1 ring-current`
                            : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Icon size={15} />
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </CardBody>
            </Card>

            {/* Priority picker */}
            <Card>
              <CardHeader title="Priority" />
              <CardBody>
                <div className="flex gap-2">
                  {priorityOptions.map(opt => {
                    const selected = parseInt(formData.priority) === opt.value
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleInputChange('priority', opt.value)}
                        className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-semibold transition-all text-center ${
                          selected
                            ? `${opt.color} ring-2 ring-offset-1 ring-current`
                            : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </CardBody>
            </Card>

            {/* Project Scope */}
            <Card>
              <CardHeader title="Project Scope" />
              <CardBody>
                <div className="flex gap-2">
                  {scopeOptions.map(opt => {
                    const checked = formData[opt.key]
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleInputChange(opt.key, !checked)}
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all text-center ${
                          checked
                            ? 'bg-primary-50 text-primary-700 border-primary-200 ring-2 ring-offset-1 ring-primary-300'
                            : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </CardBody>
            </Card>

            {/* Assignee */}
            <Card>
              <CardHeader title="Assigned To" />
              <CardBody className="space-y-3">
                {/* Quick assign buttons */}
                {quickAssignees.length > 0 && (
                  <div className="flex gap-2">
                    {quickAssignees.map(m => {
                      const mid = String(m.taskMemberId)
                      const initial = (m.name || '?')[0].toUpperCase()
                      const selected = formData.assignedToTaskMemberId === mid
                      return (
                        <button
                          key={mid}
                          type="button"
                          onClick={() => handleInputChange('assignedToTaskMemberId', selected ? '' : mid)}
                          className={`flex items-center gap-2 flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                            selected
                              ? 'bg-primary-50 text-primary-700 border-primary-300 ring-2 ring-offset-1 ring-primary-300'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                            selected ? 'bg-primary-200 text-primary-800' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {initial}
                          </span>
                          {m.name}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Full dropdown */}
                <select
                  value={formData.assignedToTaskMemberId}
                  onChange={(e) => handleInputChange('assignedToTaskMemberId', e.target.value)}
                  className="text-sm border border-gray-200 rounded-md px-2 py-1.5 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Unassigned</option>
                  {members.filter(m => m.isActive).map(m => (
                    <option key={m.taskMemberId} value={m.taskMemberId}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </CardBody>
            </Card>

            {/* Due Date */}
            <Card>
              <CardHeader title="Due Date" />
              <CardBody className="space-y-3">
                {/* Quick date presets */}
                <div className="flex flex-wrap gap-1.5">
                  {dueDatePresets.map(preset => {
                    const presetValue = preset.fn()
                    const selected = formData.dueDate === presetValue
                    return (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => handleInputChange('dueDate', selected ? '' : presetValue)}
                        className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-all ${
                          selected
                            ? 'bg-primary-50 text-primary-700 border-primary-300'
                            : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {preset.label}
                      </button>
                    )
                  })}
                  {formData.dueDate && (
                    <button
                      type="button"
                      onClick={() => handleInputChange('dueDate', '')}
                      className="px-2.5 py-1 rounded-md border border-red-200 text-xs font-medium text-red-500 hover:bg-red-50 transition-all"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Date input */}
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  className="text-sm border border-gray-200 rounded-md px-2 py-1.5 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </CardBody>
            </Card>

            {/* Submit */}
            <div className="flex flex-col gap-2">
              <Button variant="primary" type="submit" loading={loading} icon={<Save size={18} />} className="w-full justify-center">
                {isEdit ? 'Update Task' : 'Create Task'}
              </Button>
              <Button variant="secondary" onClick={() => navigate(-1)} className="w-full justify-center">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default CreateTask
